'use client';

import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Tag } from '@/types/tags';
import { tagService } from '@/lib/tag-service';
import DirectorySearch from '@/components/ui/DirectorySearch';

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Global search modal — wraps the existing DirectorySearch in "navigate" mode.
 *
 * Tags are fetched LAZILY (only the first time the modal opens) via the
 * client-safe `tagService.getAllEntityTags()` (firebase client SDK) so the
 * navbar doesn't pull the tag list on every page load. Selecting a result
 * routes to /tags/{slug} and closes the modal.
 */
export default function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const [tags, setTags] = useState<Tag[] | null>(null);

  // Lazily load tags the first time the modal opens. Gate ONLY on `tags === null`
  // (not a separate `loading` flag) so React StrictMode's dev double-mount still
  // completes: the first mount's fetch is cancelled on cleanup, and the persistent
  // second mount re-runs the effect and resolves it. `tags === null` drives the
  // loading UI; once set, reopening never refetches.
  useEffect(() => {
    if (!open || tags !== null) return;

    let cancelled = false;
    tagService
      .getAllEntityTags(300)
      .then((result) => {
        if (!cancelled) setTags(result);
      })
      .catch(() => {
        if (!cancelled) setTags([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tags]);

  // Esc to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[12vh] sm:pt-[16vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white font-display">
            <Search className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium">Search</span>
          </div>
          <span className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-neutral-800 text-[11px] text-gray-500 dark:text-gray-400 font-display">
            Esc to close
          </span>
        </div>

        <div className="p-4">
          {tags === null ? (
            <div className="flex items-center gap-2 px-2 py-8 justify-center text-gray-500 dark:text-gray-400 font-display text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading entities…
            </div>
          ) : (
            <DirectorySearch
              tags={tags}
              mode="navigate"
              placeholder="Search entities, projects, people..."
              onTagSelect={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
